/**
 * @fileoverview Signup Plaid Step Component
 * @description Plaid Link integration for the signup flow
 */

import React, { useEffect, useState, useCallback } from 'react';
import { PlaidLinkOnSuccess, PlaidLinkOnExit } from 'react-plaid-link';
import { usePlaidLinkWithLogging } from '../../../hooks/usePlaidLinkWithLogging';
import {
  VStack,
  Spinner,
  Text,
  Button,
  Box,
  Icon,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import { MdAccountBalance, MdRefresh } from 'react-icons/md';
import { signupApi, ExchangePlaidTokenDto } from '../../../services/signupApi';
import { toUserMessage } from '../../../lib/apiError';

// Must match AddBankButton in ConnectedBanksList so that when PLAID_REDIRECT_URI
// points to /status, the OAuth return is handled there.
// OAUTH_PENDING_KEY is read by /plaid/oauth-return if that route is the redirect target.
const ADD_BANK_OAUTH_KEY = 'xacto_plaid_add_bank_oauth';
const OAUTH_PENDING_KEY = 'xacto_plaid_oauth_pending';

interface SignupPlaidStepProps {
  linkToken: string;
  tenantId: string;
  accessToken: string;
  onSuccess: () => void;
  onSkip: () => void;
}

/**
 * Plaid Link step for the signup flow.
 *
 * Automatically opens Plaid Link modal and handles token exchange.
 * Supports OAuth institutions (Chase, Wells Fargo, BofA) via localStorage
 * persistence across the bank's OAuth redirect.
 */
const SignupPlaidStep: React.FC<SignupPlaidStepProps> = ({
  linkToken,
  tenantId,
  accessToken,
  onSuccess,
  onSkip,
}) => {
  const [isExchanging, setIsExchanging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasOpened, setHasOpened] = useState(false);
  const isExchangingRef = React.useRef(false);

  // Detect OAuth return: bank redirected back with oauth_state_id in the URL.
  // Stable for the lifetime of this page load.
  const isOAuthReturn = window.location.href.includes('?oauth_state_id=');

  // Persist the link token to localStorage so that:
  //   a) If PLAID_REDIRECT_URI points to /plaid/oauth-return, PlaidOAuthReturn
  //      reads OAUTH_PENDING_KEY and completes the flow there.
  //   b) If PLAID_REDIRECT_URI points to /status, ConnectedBanksList's
  //      AddBankButton picks up ADD_BANK_OAUTH_KEY and completes it there.
  //   c) If PLAID_REDIRECT_URI points back to this page, we restore the token
  //      after the page reloads (React state is lost on navigation).
  // localStorage survives App2App browser context switches; sessionStorage does not.
  useEffect(() => {
    if (linkToken && !isOAuthReturn) {
      const pendingState = { linkToken, mode: 'signup' as const, tenantId };
      localStorage.setItem(ADD_BANK_OAUTH_KEY, linkToken);
      localStorage.setItem(OAUTH_PENDING_KEY, JSON.stringify(pendingState));
      console.log('[SignupPlaidStep] link token saved to localStorage', {
        tenantId,
        linkTokenPrefix: linkToken?.slice(0, 30),
        pendingState,
      });
    }
  }, [linkToken, isOAuthReturn, tenantId]);

  // On OAuth return, restore from localStorage in case the prop is stale/null.
  const storedToken = isOAuthReturn ? localStorage.getItem(ADD_BANK_OAUTH_KEY) : null;
  const effectiveLinkToken = storedToken || linkToken;
  if (isOAuthReturn) {
    console.log('[SignupPlaidStep] OAuth return detected', {
      url: window.location.href,
      storedTokenPrefix: storedToken?.slice(0, 30) ?? null,
      propTokenPrefix: linkToken?.slice(0, 30) ?? null,
      tenantId,
      allPlaidKeys: Object.keys(localStorage).filter(k => k.startsWith('xacto_plaid')),
    });
  }

  // Plaid requires this on the OAuth return page to correlate the session.
  // Must be window.location.href raw (the exact URL received from the bank).
  // Critical: pass this to usePlaidLink ONLY on OAuth return
  const receivedRedirectUri = isOAuthReturn ? window.location.href : undefined;

  /**
   * Handle successful Plaid Link completion
   */
  const handleSuccess: PlaidLinkOnSuccess = useCallback(
    async (publicToken, metadata) => {
      if (isExchangingRef.current) return;
      isExchangingRef.current = true;
      setIsExchanging(true);
      setError(null);
      console.log('[SignupPlaidStep] onSuccess RECEIVED', {
        mode: 'signup',
        institution: metadata.institution?.name,
        institutionId: metadata.institution?.institution_id,
        publicTokenPrefix: publicToken?.slice(0, 20),
        tenantId,
        accountCount: metadata.accounts?.length,
        linkSessionId: metadata.link_session_id,
      });

      try {
        const exchangeDto: ExchangePlaidTokenDto = {
          publicToken,
          tenantId,
          institutionId: metadata.institution?.institution_id,
          institutionName: metadata.institution?.name,
          linkSessionId: metadata.link_session_id,
        };

        await signupApi.exchangeToken(exchangeDto, accessToken);

        // Bank connected — clear localStorage and advance the wizard.
        localStorage.removeItem(ADD_BANK_OAUTH_KEY);
        localStorage.removeItem(OAUTH_PENDING_KEY);
        onSuccess();
      } catch (err) {
        console.error('Failed to exchange Plaid token:', err);
        setError(toUserMessage(err, "We couldn't connect the bank account. Please try again."));
        setIsExchanging(false);
        isExchangingRef.current = false;
      }
    },
    [tenantId, accessToken, onSuccess]
  );

  /**
   * Handle Plaid Link exit (user closed modal or error)
   */
  const handleExit: PlaidLinkOnExit = useCallback(
    (err, metadata) => {
      if (err) {
        console.error('[SignupPlaidStep] onExit', {
          mode: 'signup',
          tenantId,
          error_type: err.error_type,
          error_code: err.error_code,
          error_message: err.error_message,
          display_message: err.display_message,
          request_id: metadata?.request_id,
          link_session_id: metadata?.link_session_id,
        });
        if (err.error_code === 'INVALID_LINK_TOKEN') {
          localStorage.removeItem(ADD_BANK_OAUTH_KEY);
          localStorage.removeItem(OAUTH_PENDING_KEY);
          setError('The bank connection session expired. Please skip and reconnect your bank from the dashboard.');
        } else {
          setError(err.display_message || 'An error occurred connecting your bank');
        }
      }
      localStorage.removeItem(OAUTH_PENDING_KEY);
      setHasOpened(false);
    },
    []
  );

  const { open, ready } = usePlaidLinkWithLogging({
    mode: 'signup',
    tenantId,
    token: effectiveLinkToken,
    onSuccess: handleSuccess,
    onExit: handleExit,
    ...(receivedRedirectUri && { receivedRedirectUri }),
  });

  // Auto-open Plaid Link when ready (covers both initial open and OAuth return).
  useEffect(() => {
    console.log('[SignupPlaidStep] usePlaidLink state', {
      ready,
      hasOpened,
      isExchanging,
      hasError: !!error,
      effectiveLinkTokenPrefix: effectiveLinkToken?.slice(0, 30),
      isOAuthReturn,
      receivedRedirectUri,
    });
    if (ready && !hasOpened && !isExchanging && !error) {
      console.log('[SignupPlaidStep] opening Plaid Link', { isOAuthReturn });
      setHasOpened(true);
      open();
    }
  }, [ready, hasOpened, isExchanging, error, open]);

  /**
   * Retry opening Plaid Link after an error
   */
  const handleRetry = () => {
    setError(null);
    setHasOpened(false);
    isExchangingRef.current = false;
  };

  if (error) {
    return (
      <VStack spacing={6} textAlign="center" py={8}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>

        <VStack spacing={3}>
          <Button
            leftIcon={<Icon as={MdRefresh} />}
            colorScheme="blue"
            onClick={handleRetry}
          >
            Try Again
          </Button>
          <Button variant="ghost" onClick={onSkip}>
            Skip for now
          </Button>
        </VStack>
      </VStack>
    );
  }

  if (isExchanging) {
    return (
      <VStack spacing={6} textAlign="center" py={8}>
        <Spinner size="xl" color="blue.500" thickness="4px" />
        <Text fontSize="lg" fontWeight="medium">
          Securing your bank connection...
        </Text>
        <Text color="gray.500">
          Please wait while we complete the setup
        </Text>
      </VStack>
    );
  }

  // While processing an OAuth return show a spinner instead of the button —
  // Link auto-opens and the user doesn't need to interact again.
  if (isOAuthReturn) {
    return (
      <VStack spacing={6} textAlign="center" py={8}>
        <Spinner size="xl" color="blue.500" thickness="4px" />
        <Text fontSize="lg" fontWeight="medium">
          Completing bank connection...
        </Text>
        <Text color="gray.500">
          Please wait while we finish connecting your bank
        </Text>
      </VStack>
    );
  }

  return (
    <VStack spacing={6} textAlign="center" py={8}>
      <Box
        p={4}
        borderRadius="full"
        bg="blue.50"
        _dark={{ bg: 'blue.900' }}
      >
        <Icon as={MdAccountBalance} boxSize={12} color="blue.500" />
      </Box>

      {!ready ? (
        <>
          <Spinner size="lg" color="blue.500" />
          <Text fontSize="lg" fontWeight="medium">
            Preparing bank connection...
          </Text>
        </>
      ) : (
        <>
          <Text fontSize="lg" fontWeight="medium">
            Connect Your Bank Account
          </Text>
          <Text color="gray.500" maxW="md">
            Securely link your bank account to enable financial services.
            Your credentials are never shared with us.
          </Text>

          <VStack spacing={3} pt={4}>
            <Button
              size="lg"
              colorScheme="blue"
              onClick={() => open()}
              leftIcon={<Icon as={MdAccountBalance} />}
            >
              Connect Bank
            </Button>
            <Button variant="ghost" size="sm" onClick={onSkip}>
              Skip for now
            </Button>
          </VStack>

          <Text fontSize="xs" color="gray.400" maxW="sm">
            You can always connect your bank later from your dashboard
          </Text>
        </>
      )}
    </VStack>
  );
};

export default SignupPlaidStep;
